import React, {useEffect, useState} from 'react'
import {
  Box,
  Stack,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  TextField,
  Autocomplete,
  Checkbox,
  RadioGroup,
  Radio,
  FormControl,
  FormControlLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material'
import {useSnackbar} from 'notistack'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import {
  FormCard,
  FormHeader,
  FormHeaderIconBox,
  FormHeaderTextGroup,
  FormSectionTitle,
  FormSectionSubtitle,
  FormBody,
  FieldHelper,
  FormDivider,
  FormFooter,
  FormFooterHint,
  RunButton,
  ResultsBanner,
  ResultsBannerBody,
  ResultsBannerText,
  ResultsBannerActions,
  BannerActionButton,
} from './styled'

import {useBulkServiceability, PD_SERVICE_TYPES, PD_PAYMENT_MODES} from './useBulkServiceability'
import DownloadSampleFile from '../../components/DownloadSampleFile'

const STEPS = ['Select Courier', 'Select Account Codes', 'Service Type & Mode', 'Upload & Validate', 'Confirm']
const MAX_FILE_BYTES = 5 * 1024 * 1024 // 5 MB

export default function PdUploadWizard({parentCps = [], onUploadComplete}) {
  const {enqueueSnackbar} = useSnackbar()
  const {fetchAccountCodes, dryRunPdUpload, confirmPdUpload} = useBulkServiceability()

  const [step, setStep] = useState(0)
  const [parentCp, setParentCp] = useState('')
  const [accountCodes, setAccountCodes] = useState([])
  const [isLoadingCodes, setIsLoadingCodes] = useState(false)
  const [selectedAccountCodes, setSelectedAccountCodes] = useState([])
  const [serviceType, setServiceType] = useState(PD_SERVICE_TYPES.SDD)
  const [paymentMode, setPaymentMode] = useState(PD_PAYMENT_MODES.prepaid)
  const [file, setFile] = useState(null)
  const [dryRun, setDryRun] = useState(null)
  const [isValidating, setIsValidating] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState(null)

  useEffect(() => {
    setAccountCodes([])
    setSelectedAccountCodes([])
    if (!parentCp) return
    let cancelled = false
    setIsLoadingCodes(true)
    fetchAccountCodes(parentCp).then((data) => {
      if (cancelled) return
      setAccountCodes(data?.accountCodes || [])
      setIsLoadingCodes(false)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCp])

  const allSelected =
    accountCodes.length > 0 && selectedAccountCodes.length === accountCodes.length
  const toggleAll = () =>
    setSelectedAccountCodes(allSelected ? [] : accountCodes.map((c) => c.accountCode))
  const toggleOne = (code) =>
    setSelectedAccountCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    )

  const handleFileChange = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (!f.name.toLowerCase().endsWith('.csv')) {
      enqueueSnackbar('Please upload a .csv file', {variant: 'warning'})
      return
    }
    if (f.size > MAX_FILE_BYTES) {
      enqueueSnackbar('File exceeds 5 MB', {variant: 'warning'})
      return
    }
    setFile(f)
    setDryRun(null)
  }

  const runDryRun = async () => {
    if (!file) {
      enqueueSnackbar('Choose a CSV first', {variant: 'warning'})
      return
    }
    setIsValidating(true)
    const data = await dryRunPdUpload(file)
    setIsValidating(false)
    if (data) {
      setDryRun(data)
      if ((data.totalPairs || 0) === 0) {
        enqueueSnackbar('No valid pickup→drop pairs — please fix and try again', {variant: 'warning'})
      }
    }
  }

  const onConfirm = async () => {
    if (!dryRun || !file) return
    setIsUploading(true)
    const data = await confirmPdUpload({
      file,
      courierParent: parentCp,
      serviceType,
      paymentMode,
      courierAccountCodes: selectedAccountCodes,
    })
    setIsUploading(false)
    if (data) {
      setUploadResult(data)
      onUploadComplete?.(data)
    }
  }

  const startOver = () => {
    setStep(0)
    setParentCp('')
    setAccountCodes([])
    setSelectedAccountCodes([])
    setServiceType(PD_SERVICE_TYPES.SDD)
    setPaymentMode(PD_PAYMENT_MODES.prepaid)
    setFile(null)
    setDryRun(null)
    setUploadResult(null)
  }

  const canGoStep2 = !!parentCp
  const canGoStep3 = selectedAccountCodes.length > 0
  const canGoStep4 = !!serviceType && !!paymentMode
  const canGoStep5 = !!dryRun && (dryRun.totalPairs || 0) > 0

  return (
    <FormCard>
      <FormHeader>
        <FormHeaderIconBox>
          <UploadFileOutlinedIcon fontSize='small' />
        </FormHeaderIconBox>
        <FormHeaderTextGroup>
          <FormSectionTitle>SDD / NDD (PD) Pincode Upload</FormSectionTitle>
          <FormSectionSubtitle>
            Upload pickup→drop pincode PAIRS for Same-Day / Next-Day serviceability — fans out to all selected account codes.
          </FormSectionSubtitle>
        </FormHeaderTextGroup>
        {(parentCp || file) && (
          <Box sx={{ml: 'auto'}}>
            <Button
              size='small'
              startIcon={<RestartAltIcon fontSize='small' />}
              onClick={startOver}
              sx={{textTransform: 'none', fontSize: '0.75rem', fontWeight: 600}}
            >
              Start over
            </Button>
          </Box>
        )}
      </FormHeader>

      <FormBody>
        <Stepper activeStep={step} sx={{mb: 3}}>
          {STEPS.map((label, idx) => (
            <Step key={label} completed={step > idx}>
              <StepLabel>
                <Typography sx={{fontSize: '0.72rem', fontWeight: 600}}>{label}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* ── STEP 1 ── */}
        {step === 0 && (
          <Box>
            <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 1}}>
              Step 1 — Select Courier Parent
            </Typography>
            <Autocomplete
              size='small'
              fullWidth
              options={parentCps}
              value={parentCp || null}
              onChange={(_, v) => setParentCp(v || '')}
              renderInput={(params) => (
                <TextField {...params} label='Parent Courier' placeholder='Pick a courier' />
              )}
              sx={{maxWidth: 400}}
            />
            <FieldHelper sx={{mt: 1}}>
              PD upload fans out to all child account codes you select in the next step.
            </FieldHelper>
          </Box>
        )}

        {/* ── STEP 2 ── */}
        {step === 1 && (
          <Box>
            <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{mb: 1.5}}>
              <Typography sx={{fontSize: '0.85rem', fontWeight: 700}}>
                Step 2 — Select Account Codes ({selectedAccountCodes.length} of {accountCodes.length} selected)
              </Typography>
              <Button
                size='small'
                onClick={toggleAll}
                disabled={isLoadingCodes || accountCodes.length === 0}
                sx={{textTransform: 'none', fontWeight: 600}}
              >
                {allSelected ? 'Clear all' : 'Select all'}
              </Button>
            </Stack>
            {isLoadingCodes ? (
              <Stack alignItems='center' sx={{py: 4}}>
                <CircularProgress size={24} />
              </Stack>
            ) : accountCodes.length === 0 ? (
              <Typography sx={{fontSize: '0.8rem', color: 'grey.600'}}>
                No account codes available for this parent.
              </Typography>
            ) : (
              <TableContainer
                sx={{maxHeight: 360, border: '1px solid', borderColor: 'grey.200', borderRadius: 1.5}}
              >
                <Table size='small' stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding='checkbox' />
                      <TableCell sx={{fontSize: '0.72rem', fontWeight: 700}}>Account Code</TableCell>
                      <TableCell sx={{fontSize: '0.72rem', fontWeight: 700}}>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accountCodes.map((c) => {
                      const checked = selectedAccountCodes.includes(c.accountCode)
                      return (
                        <TableRow
                          key={c.accountCode}
                          hover
                          onClick={() => toggleOne(c.accountCode)}
                          sx={{cursor: 'pointer'}}
                        >
                          <TableCell padding='checkbox'>
                            <Checkbox size='small' checked={checked} />
                          </TableCell>
                          <TableCell sx={{fontSize: '0.78rem', fontFamily: 'monospace'}}>
                            {c.accountCode}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size='small'
                              label={c.courierType}
                              sx={{height: 20, fontSize: '0.65rem', fontWeight: 700}}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* ── STEP 3 ── service type + payment mode */}
        {step === 2 && (
          <Box>
            <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 1.5}}>
              Step 3 — Service Type &amp; Payment Mode
            </Typography>
            <Stack direction='row' spacing={6}>
              <FormControl>
                <Typography sx={{fontSize: '0.72rem', fontWeight: 700, color: 'grey.700', mb: 0.5}}>
                  SERVICE TYPE
                </Typography>
                <RadioGroup value={serviceType} onChange={(_, v) => setServiceType(v)}>
                  <FormControlLabel value={PD_SERVICE_TYPES.SDD} control={<Radio size='small' />} label='SDD (Same-Day)' />
                  <FormControlLabel value={PD_SERVICE_TYPES.NDD} control={<Radio size='small' />} label='NDD (Next-Day)' />
                </RadioGroup>
              </FormControl>
              <FormControl>
                <Typography sx={{fontSize: '0.72rem', fontWeight: 700, color: 'grey.700', mb: 0.5}}>
                  PAYMENT MODE
                </Typography>
                <RadioGroup value={paymentMode} onChange={(_, v) => setPaymentMode(v)}>
                  <FormControlLabel value={PD_PAYMENT_MODES.prepaid} control={<Radio size='small' />} label='Prepaid' />
                  <FormControlLabel value={PD_PAYMENT_MODES.cod} control={<Radio size='small' />} label='COD' />
                </RadioGroup>
              </FormControl>
            </Stack>
            <FieldHelper sx={{mt: 1}}>
              One upload per (Service Type × Payment Mode). It replaces existing PD pincodes for the selected couriers for this combo.
            </FieldHelper>
          </Box>
        )}

        {/* ── STEP 4 ── upload & validate */}
        {step === 3 && (
          <Box>
            <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 1}}>
              Step 4 — Upload &amp; Validate
            </Typography>
            <Box
              sx={{
                p: 2,
                border: '1px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
                textAlign: 'center',
                mb: 2,
              }}
            >
              <input
                type='file'
                accept='.csv'
                onChange={handleFileChange}
                style={{display: 'none'}}
                id='pd-serv-file-input'
              />
              <label htmlFor='pd-serv-file-input'>
                <Button component='span' variant='outlined' size='small' startIcon={<UploadFileOutlinedIcon />}>
                  {file ? 'Choose different file' : 'Choose CSV file'}
                </Button>
              </label>
              {file && (
                <Typography sx={{mt: 1, fontSize: '0.78rem', color: 'grey.700'}}>
                  📄 <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
              <Typography sx={{mt: 0.5, fontSize: '0.65rem', color: 'grey.500'}}>
                Expected: two columns with header <code>pickup_pincode,drop_pincode</code>, max 5 MB.
              </Typography>
            </Box>

            <Stack direction='row' spacing={1} alignItems='center'>
              <RunButton
                variant='contained'
                size='small'
                onClick={runDryRun}
                disabled={!file || isValidating}
                startIcon={
                  isValidating ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon fontSize='small' />
                }
              >
                Validate CSV
              </RunButton>
              <DownloadSampleFile
                fileName='PD_SERVICEABILITY_PINCODE_SAMPLE'
                label='Download Sample'
                size='small'
                variant='outlined'
              />
            </Stack>

            {dryRun && (
              <Box sx={{mt: 2}}>
                <Stack direction='row' spacing={2} sx={{mb: 1.5}}>
                  <SummaryStat label='Total rows' value={dryRun.totalRowsInFile} />
                  <SummaryStat label='Pickups' value={dryRun.pickupCount} />
                  <SummaryStat
                    label='Pairs'
                    value={dryRun.totalPairs}
                    icon={<CheckCircleOutlineIcon color='success' fontSize='small' />}
                  />
                  <SummaryStat
                    label='Invalid rows'
                    value={dryRun.invalidRows}
                    icon={<WarningAmberIcon color='warning' fontSize='small' />}
                  />
                </Stack>
                {dryRun.previewPickups?.length > 0 && (
                  <Box>
                    <Typography sx={{fontSize: '0.7rem', fontWeight: 600, color: 'grey.700', mb: 0.5}}>
                      PREVIEW PICKUPS
                    </Typography>
                    <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                      {dryRun.previewPickups.map((p) => (
                        <Chip key={p} size='small' label={p} sx={{fontSize: '0.7rem'}} />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ── STEP 5 ── confirm */}
        {step === 4 && (
          <Box>
            <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 1.5}}>
              Step 5 — Confirm Upload
            </Typography>

            {!uploadResult && (
              <Box
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  border: '1px solid #c8e6c9',
                  backgroundColor: '#f1f8e9',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{flex: 1, minWidth: 220}}>
                  <Typography sx={{fontSize: '0.85rem', fontWeight: 700, color: '#1b5e20'}}>
                    Ready to upload
                  </Typography>
                  <Typography sx={{fontSize: '0.75rem', color: 'grey.700', mt: 0.25}}>
                    <strong>{dryRun?.totalPairs?.toLocaleString()}</strong> pickup→drop pairs (
                    <strong>{dryRun?.pickupCount}</strong> pickups) will be saved to{' '}
                    <strong>{selectedAccountCodes.length}</strong> account code(s) under{' '}
                    <strong>{parentCp}</strong> as <strong>{serviceType} / {paymentMode}</strong>.
                  </Typography>
                  <Typography sx={{fontSize: '0.68rem', color: 'grey.600', fontStyle: 'italic', mt: 0.5}}>
                    Replaces existing PD pincodes for the selected account codes for this service type + payment mode.
                  </Typography>
                </Box>
                <RunButton
                  variant='contained'
                  color='success'
                  size='small'
                  startIcon={
                    isUploading ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon fontSize='small' />
                  }
                  onClick={onConfirm}
                  disabled={isUploading}
                >
                  Confirm Upload
                </RunButton>
              </Box>
            )}

            {isUploading && (
              <Box sx={{mb: 2}}>
                <Typography sx={{fontSize: '0.78rem', color: 'grey.700', mb: 0.5}}>
                  Fanning out to {selectedAccountCodes.length} account code(s)…
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {uploadResult && (
              <ResultsBanner
                severity={
                  uploadResult.status === 'COMPLETED'
                    ? 'success'
                    : uploadResult.status === 'PARTIAL'
                    ? 'warning'
                    : 'error'
                }
              >
                <ResultsBannerBody>
                  <ResultsBannerText>
                    <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 0.25}}>
                      {uploadResult.status === 'COMPLETED'
                        ? `✅ Upload complete — ${uploadResult.successful}/${uploadResult.totalAccountCodes} account codes updated`
                        : uploadResult.status === 'PARTIAL'
                        ? `⚠ Partial success — ${uploadResult.successful}/${uploadResult.totalAccountCodes} succeeded`
                        : `❌ Upload failed`}
                    </Typography>
                    <Typography sx={{fontSize: '0.75rem'}}>
                      {uploadResult.serviceType} / {uploadResult.paymentMode} ·{' '}
                      {uploadResult.pickupCount?.toLocaleString()} pickups
                    </Typography>
                  </ResultsBannerText>
                  <ResultsBannerActions>
                    <BannerActionButton variant='contained' color='primary' size='small' onClick={startOver}>
                      Start new upload
                    </BannerActionButton>
                  </ResultsBannerActions>
                </ResultsBannerBody>
              </ResultsBanner>
            )}
          </Box>
        )}

        <FormDivider />

        <FormFooter>
          <FormFooterHint>Step {step + 1} of 5</FormFooterHint>
          <Stack direction='row' spacing={1}>
            {step > 0 && !uploadResult && (
              <Button
                size='small'
                onClick={() => setStep((s) => s - 1)}
                sx={{textTransform: 'none', fontWeight: 600}}
              >
                Back
              </Button>
            )}
            {step < 4 && (
              <RunButton
                variant='contained'
                size='small'
                onClick={() => setStep((s) => s + 1)}
                disabled={
                  (step === 0 && !canGoStep2) ||
                  (step === 1 && !canGoStep3) ||
                  (step === 2 && !canGoStep4) ||
                  (step === 3 && !canGoStep5)
                }
              >
                {step === 3 ? 'Continue to Confirm' : 'Next'}
              </RunButton>
            )}
          </Stack>
        </FormFooter>
      </FormBody>
    </FormCard>
  )
}

function SummaryStat({label, value, icon}) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.62rem',
          fontWeight: 600,
          color: 'grey.600',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}
      >
        {label}
      </Typography>
      <Stack direction='row' alignItems='center' spacing={0.5}>
        {icon}
        <Typography sx={{fontSize: '1.1rem', fontWeight: 800}}>
          {(value ?? 0).toLocaleString()}
        </Typography>
      </Stack>
    </Box>
  )
}
